import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useGetLoggedInUserQuery, useGetUsersQuery } from '../redux/api/users';
import { RootState } from '../redux/store';

/**
 * Unified control disable logic:
 * - Returns controlsDisabled when user is NOT a Shop Manager and status is Completed.
 * - isShopManager is tri-state: true | false | undefined (undefined while loading/unknown).
 */
export const useControlsDisabled = (
  lineItemStatus?: string
): { controlsDisabled: boolean; isShopManager: boolean | undefined } => {
  const authToken = useSelector((state: RootState) => state.user.authToken);

  const { data: loggedInUser, refetch: refetchMe } = useGetLoggedInUserQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: shopManagersA, refetch: refetchA } = useGetUsersQuery(
    { groups__name: 'Shop Manager' },
    { refetchOnMountOrArgChange: true }
  );
  const { data: shopManagersB, refetch: refetchB } = useGetUsersQuery(
    { groups__name: 'Shop Managers' },
    { refetchOnMountOrArgChange: true }
  );

  // Ensure fresh permission data after login/logout (token changes)
  useEffect(() => {
    // Trigger refetches whenever auth token changes
    // (e.g., user switches accounts without hard reload)
    refetchMe();
    refetchA();
    refetchB();
  }, [authToken, refetchMe, refetchA, refetchB]);

  const isShopManager = loggedInUser
    ? ((shopManagersA?.results || []).some((u) => u.id === loggedInUser.id) ||
       (shopManagersB?.results || []).some((u) => u.id === loggedInUser.id))
    : undefined;

  const controlsDisabled = isShopManager === false && lineItemStatus === 'Completed';

  return { controlsDisabled, isShopManager };
};

export default useControlsDisabled;
